import { useTranslation } from 'react-i18next';

import { getCodedAnnotationPresentation } from 'services/webediErrors/annotationPresentation';
import { WebEDIUnmatchedArticleWarning } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { TransactionState } from 'support/types';
import { WithRef } from 'support/types/withRef';

import { ActionsSection } from '../../components/ActionsSection';
import { AddToMasterdataButton } from '../../components/AddToMasterdataButton/AddToMasterdataButton';
import { EditDocumentButton } from '../../components/EditDocumentButton/EditDocumentButton';
import { MasterdataLink } from '../../components/MasterdataLink/MasterdataLink';
import { NavigationFooter } from '../../components/NavigationFooter';
import { FieldAnnotationPopoverShell } from '../../FieldAnnotationPopoverShell';

type Props = {
  annotation: WebEDIUnmatchedArticleWarning;
  viewMode: boolean;
  style?: React.CSSProperties;
};

export const UnmatchedArticleWarning = ({ annotation, viewMode, ref, style }: WithRef<Props, HTMLDivElement>) => {
  const { t } = useTranslation();
  const isDelivered = useWebEDIStore((state) => state.messageState === TransactionState.success);

  const { name, identifiers } = annotation.currentValue;
  const descriptionValue =
    name || identifiers.gtin || identifiers.sender_internal || identifiers.receiver_internal || '';

  return (
    <FieldAnnotationPopoverShell
      ref={ref}
      style={style}
      status="warning"
      title={t(getCodedAnnotationPresentation(annotation).titleKey)}
      description={t('webedi:fieldAnnotation.unmatchedArticleWarning.description', { value: descriptionValue })}
      info={<MasterdataLink label={t('webedi:fieldAnnotation.unmatchedArticleWarning.viewMasterdata')} />}
      actions={
        isDelivered ? undefined : (
          <ActionsSection>
            <AddToMasterdataButton annotationId={annotation.annotationId ?? annotation.id} />
            <EditDocumentButton viewMode={viewMode} annotation={annotation} />
          </ActionsSection>
        )
      }
      footer={<NavigationFooter />}
    />
  );
};

/**
 * i18n keys
 * t('webedi:fieldAnnotation.unmatchedArticleWarning.title')
 */
